import { Accordion, AccordionDetails, FormGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import BottomLine from "~/src/components/BottomLine";
import CountCircle from "~/src/components/CountCirle";
import Checkbox from "~/src/components/Form/Checkbox";
import Text, { TextVariant } from "~/src/components/Text/Text";
import { ArtType } from "~/src/helpers/types";
import { useStateful } from "~/src/hooks/useStateful";

interface props {
  selected: FilterType[];
  onToggle: (toggled: FilterType) => void;
  showElgHjortOptions: boolean;
  showMineRegOption: boolean;
  disabled: boolean;
}
export enum FilterType {
  Elg = ArtType.Elg,
  Hjort = ArtType.Hjort,
  MineReg = "Mine registreringer",
  VarslerReg = "Registreringer med varsler",
}
export default function FilterCombobox({
  selected,
  onToggle,
  showElgHjortOptions,
  showMineRegOption,
  disabled,
}: props) {
  const expanded = useStateful<boolean>(false);
  const { t } = useTranslation();

  function CheckBoxWLabel({ label, value }: { label?: string; value: FilterType }) {
    return (
      <Checkbox
        label={label ?? value.valueOf()}
        value={value}
        onChange={() => {
          onToggle(value);
        }}
        checked={selected.includes(value)}
        small
      />
    );
  }

  return (
    <Accordion
      expanded={expanded.value}
      disabled={disabled}
      className="shadow-none bg-ldir-blue-800 text-ldir-brown-900"
    >
      <button
        className={`pl-4 pr-0 py-3.5 bg-ldir-brown-300 rounded border-ldir-blue-900 border-2 flex w-full justify-between items-center ${
          disabled ? "opacity-50" : ""
        }`}
        aria-controls="filter-content"
        id="filter-header"
        type="button"
        disabled={disabled}
        aria-disabled={disabled}
        onClick={() => {
          if (!disabled) expanded.set(!expanded.value);
        }}
      >
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="20"
            viewBox="0 0 30 20"
            fill="none"
            role="graphics-symbol"
          >
            <path
              d="M11.6667 20V16.6667H18.3333V20H11.6667ZM5 11.6667V8.33333H25V11.6667H5ZM0 3.33333V0H30V3.33333H0Z"
              fill="#006178"
            />
          </svg>
          <Text variant={TextVariant.large} className="ml-2 text-left">
            {t("pages.oversikt.filter")}
          </Text>
        </div>
        <div className="mx-4 flex items-center gap-2">
          <CountCircle
            hideZero
            count={
              selected.length -
              (!showElgHjortOptions && selected.includes(FilterType.Elg) ? 1 : 0) -
              (!showElgHjortOptions && selected.includes(FilterType.Hjort) ? 1 : 0) -
              (!showMineRegOption && selected.includes(FilterType.MineReg) ? 1 : 0)
            }
          />
          <svg
            className={` ${expanded.value ? "rotate-180" : ""}`}
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="15"
            viewBox="0 0 30 15"
            fill="none"
            role="graphics-symbol"
          >
            <path d="M30 0L15 15L1.31134e-06 -2.62268e-06L30 0Z" fill="#006178" />
          </svg>
        </div>
      </button>
      <AccordionDetails id="filter-content" className="bg-ldir-blue-800 p-0">
        <div className="h-2" />
        <div className="bg-ldir-brown-300 rounded shadow-[0px_4px_4px_rgba(0,0,0,0.25)] p-4">
          <FormGroup>
            {showElgHjortOptions && (
              <>
                <CheckBoxWLabel label={t("fauna.art.elg.artsnavn")} value={FilterType.Elg} />
                <CheckBoxWLabel label={t("fauna.art.hjort.artsnavn")} value={FilterType.Hjort} />
                <BottomLine className="mb-4" />
              </>
            )}
            {showMineRegOption && (
              <CheckBoxWLabel
                label={t("pages.oversikt.filters.mineReg")}
                value={FilterType.MineReg}
              />
            )}
            <CheckBoxWLabel
              label={t("pages.oversikt.filters.varsler")}
              value={FilterType.VarslerReg}
            />
          </FormGroup>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
